'use client';

import { sleep } from '@/lib/util/promise';
import Button from '@/ui/Button/Button';
import Input from '@/ui/FormElements/Input';
import Chip from '@/ui/Tags/Chip';
import TagList from '@/ui/Tags/TagList';
import { Plus } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import styles from './SearchForm.module.scss';

const HiddenInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input
    {...props}
    style={{
      position: 'absolute',
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: 0,
      ...(props.style ?? {}),
    }}
  />
);

export type SearchFormProps = {
  query?: string;
  tags?: string[];
};

const SearchForm: React.FC<SearchFormProps> = ({ query = '', tags = [] }) => {
  const router = useRouter();
  const pathname = usePathname();

  const formRef = useRef<HTMLFormElement>(null);

  const [currentTags, setCurrentTags] = useState([...tags]);
  const [currentQuery, setCurrentQuery] = useState(query);
  const [currentTagInput, setCurrentTagInput] = useState('');

  async function addTag(tag: string) {
    if (!currentTags.includes(tag.toLowerCase())) {
      setCurrentTags([...currentTags, tag.toLowerCase()].toSorted());
    }
    await sleep(10);
    submit();
  }

  async function removeTag(tag: string) {
    setCurrentTags(currentTags.filter((x) => x.toLowerCase() !== tag.toLowerCase()));
    await sleep(10);
    submit();
  }

  function handleTagEnter(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && currentTagInput) {
      addTag(currentTagInput);
      setCurrentTagInput('');
    }
  }

  function submit() {
    formRef.current?.requestSubmit();
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const next = new URLSearchParams();

    setCurrentTags((tags) => tags.filter((x) => !!x.trim()));

    if (currentQuery) next.set('q', currentQuery);
    if (currentTags) next.set('tags', currentTags.join(','));

    router.replace(`${pathname}?${next.toString()}`, { scroll: false });
  }

  return (
    <form ref={formRef} className={styles.form} onSubmit={handleSubmit}>
      <span className={styles.heading}>Search by text</span>
      <Input
        name="query"
        value={currentQuery}
        placeholder="e.g., Full-stack developer"
        className={styles.input}
        onChange={(e) => setCurrentQuery(e.target.value)}
      />

      <span className={styles.heading}>Filter by tags</span>

      <div className={styles.tagsInputContainer}>
        <Input
          value={currentTagInput}
          placeholder="e.g., Full-stack developer"
          className={styles.input}
          onKeyDown={handleTagEnter}
          onChange={(e) => setCurrentTagInput(e.target.value)}
        />
        <Button
          variant="outlined"
          style={{ padding: '3px 8px' }}
          onClick={() => {
            addTag(currentTagInput);
            setCurrentTagInput('');
          }}
        >
          <Plus />
        </Button>
      </div>
      <TagList>
        {currentTags.map((tag) => (
          <Chip key={tag} deletable={true} onDelete={() => removeTag(tag)}>
            {tag}
          </Chip>
        ))}
      </TagList>

      <Button type="submit" value="submit">
        Search
      </Button>

      {/* Hidden inputs */}
      <HiddenInput readOnly name="tags" value={currentTags} />
    </form>
  );
};

export default SearchForm;
